/**
 * Pile list for /trees-pipeline?stage=… only.
 * Bare /trees-pipeline is the swimlane board. The hopper overview stole that
 * URL twice; do not bind treesPipelineRequested() to the scoreboard again.
 */
import { formatInteger } from "./format.js";
import { escapeAttr, escapeHtml, labelize, pill } from "./dashboard-html.js";
import type {
  PropertiesConveyorHealth,
  TreesPipelineAuctionTimingCounts,
  TreesPipelineCounts
} from "./dashboard-command-center.js";
import type { TreesPipelineList } from "./dashboard-properties-conveyor.js";

export function treesPipelineRequested(pathname?: string, search?: string): boolean {
  const path = (pathname ?? (typeof window === "undefined" ? "/" : window.location.pathname)).replace(/\/+$/, "") || "/";
  if (path !== "/trees-pipeline") return false;
  return treesPipelineListQuery(search) != null;
}

export function treesPipelineListQuery(search?: string): { stage: string; deadend: string | null } | null {
  const query = search ?? (typeof window === "undefined" ? "" : window.location.search);
  const params = new URLSearchParams(query.startsWith("?") ? query.slice(1) : query);
  const stage = params.get("stage")?.trim() ?? "";
  if (!stage) return null;
  return { stage, deadend: params.get("deadend")?.trim() || null };
}

export function treesPipelineListHref(stage: string, deadend?: string | null): string {
  const params = new URLSearchParams({ stage });
  if (deadend) params.set("deadend", deadend);
  return `/trees-pipeline?${params}`;
}

export async function renderTreesPipelineOverview(mount: HTMLElement): Promise<void> {
  const { fetchPropertiesConveyorHealth, fetchPropertiesConveyorList } = await import("./dashboard-properties-conveyor.js");
  const list = treesPipelineListQuery();
  if (list) {
    const payload = await fetchPropertiesConveyorList(list.stage, list.deadend);
    mount.innerHTML = treesPipelineListHtml(payload, list);
    return;
  }
  const health = await fetchPropertiesConveyorHealth();
  mount.innerHTML = treesPipelineOverviewHtml(health);
}

const TREE_SOURCE_ORDER = [
  "logs_upcoming",
  "logs_held",
  "brock",
  "hutchens",
  "ingle",
  "bell",
  "mtg",
  "catawba",
  "logs"
] as const;

const TREE_SOURCE_LABELS: Record<string, string> = {
  brock: "Brock and Scott",
  hutchens: "Hutchens",
  ingle: "Ingle",
  catawba: "Catawba",
  bell: "Bell Carrington",
  logs: "LOGS",
  logs_upcoming: "Logs upcoming",
  logs_held: "Logs held",
  mtg: "MTG"
};

const STAGE_COPY: Record<string, string> = {
  auction_window: "Need a fresh eCourts auction date before enrichment.",
  waiting: "Auction is more than 30 days out.",
  closed: "Sale cancelled, auction closed, or invalid owner name.",
  need_owner: "No person name yet. ReportAll has to find the owner.",
  need_skip: "Named. Waiting on SmartSkip for phones and deceased.",
  need_value: "Skip is on the row. Still need a real AVM and EPLV.",
  need_plv: "Market value is on the row. Still need present loan value.",
  need_equity: "Value and EPLV are on the row. Still need equity.",
  need_close: "Close create or status delivery is still open.",
  need_obit: "Already in Close. Obituary is not confirmed.",
  ready_to_call: "Close confirmed Ready to Call.",
  on_hold: "Close confirmed On Hold, or the sale is cancelled/complete.",
  blocked: "Not deceased, or equity is below $25k."
};

export const TREES_PIPELINE_DEADEND_COPY: Record<string, string> = {
  reportall_upstream_unavailable: "ReportAll lookup failed. Includes stale stamps from the expired key.",
  reportall_no_match: "ReportAll already came back empty.",
  blocked_no_owner: "ReportAll found a name we cannot use (LLC, trust, estate, bank, HOA).",
  skip_row_absent: "SmartSkip already came back empty.",
  awaiting_skip_trace: "SmartSkip was sent; no result landed on the house.",
  smartskip_payment_card_mismatch: "Saved payment card does not match the SmartSkip account.",
  smartskip_not_configured: "SmartSkip is not configured.",
  local_browser_generation_stopped: "Local Chromium cannot launch because this generation is not accepting work.",
  local_browser_unavailable: "Local Chromium is not available for this hop.",
  smartskip_login_failed: "SmartSkip login failed. Tool is paused.",
  smartskip_checkout_failed: "SmartSkip checkout failed. Tool is paused.",
  smartskip_schema_changed: "SmartSkip page layout changed.",
  smartskip_timeout: "SmartSkip timed out.",
  smartskip_upstream_unavailable: "SmartSkip was unavailable.",
  attom_owner_upstream_unauthorized: "ATTOM rejected the owner credential. Tool is paused; run ReportAll.",
  property_value_lookup_pending: "Market-value lookup is still pending.",
  attom_avm_address_mismatch: "ATTOM authenticated, but this address did not match.",
  attom_avm_no_match: "ATTOM found no AVM for this address.",
  property_value_no_match: "Zillow found no Zestimate.",
  blocked_no_value: "No market value from Zillow or ATTOM.",
  close_unexpected: "Close write failed.",
  close_lead_not_found: "Stored Close lead id was not found. Relink from a verified address match; Create will not pick a row that still has an id.",
  close_lead_ambiguous: "Multiple Close leads matched this address. Review candidate ids in the blocked reason; do not create a duplicate.",
  close_status_invalid: "Close rejected the status id. Tool is paused.",
  close_status_unresolved: "Close Ready to Call / On Hold status is missing from the org.",
  obituary_no_match: "No obituary match.",
  obituary_ambiguous_review: "Obituary needs a human look.",
  obituary_operator_batched: "Obituary discovery is paused.",
  obituary_provider_rate_limited: "SerpAPI rate-limited the search. This is not out of searches.",
  obituary_research_disabled: "Obituary research is off.",
  obituary_not_configured: "Obituary lookup is not configured.",
  blocked_no_plv: "Missing present loan value.",
  ecourts_sp_plv_page_miss: "eCourts page had no remaining principal / present loan value.",
  blocked_no_equity: "Equity could not be calculated.",
  over_30_days: "Auction >30 days",
  past_or_unknown: "Auction date is past or missing.",
  auction_date_refresh_pending: "Auction date refresh has not landed.",
  ecourts_missing_required_pdfs: "Retrieved case is missing required PDFs.",
  ecourts_upsert_pending: "Stored eCourts case has not been written onto the house.",
  sale_cancelled: "Sale Cancelled",
  sale_complete: "Auction Closed",
  invalid_owner_name: "Invalid Owner name",
  given_to_pi: "Given to PI.",
  given_to_genealogist: "Given to genealogist.",
  follow_up: "Follow-up.",
  passed: "Passed.",
  on_hold: "Parked on hold.",
  blocked_unclassified: "Blocked without a typed reason.",
  close_not_deceased: "Owner is not deceased.",
  close_equity_not_finite: "Estimated equity is missing.",
  close_equity_below_threshold: "Equity is below the Close minimum.",
  close_obituary_required: "Close create requires an obituary URL.",
  blocked_equity_below_25k: "Equity is below $25k."
};

const WORK_STAGE_IDS = ["need_owner", "need_skip", "need_value", "need_plv", "need_equity", "need_close", "need_obit"];

export function treesPipelineOverviewHtml(health: PropertiesConveyorHealth | null): string {
  const tree = health?.tree ?? null;
  const generated = health?.generatedAt ? new Date(health.generatedAt).toLocaleString() : "not loaded";
  return `
    <main class="trees-pipeline-page" data-trees-pipeline-overview>
      ${tree ? treesPipelineLiveHtml(tree, generated) : treesPipelineUnavailableHtml()}
    </main>
  `;
}

function treesPipelineLiveHtml(tree: TreesPipelineCounts, generated: string): string {
  const work = nextWork(tree);
  const latestDay = formatDay(tree.latestScrapeAt);
  const activeStages = tree.stages.filter((stage) => stage.count > 0);
  return `
      <header class="trees-pipeline-top">
        <div>
          <p class="eyebrow">Seven trees · exclusive hops</p>
          <h1>Trees pipeline</h1>
        </div>
        <div class="trees-pipeline-top-actions">
          <a class="button secondary" href="/">Dashboard</a>
          ${pill(work ? work.label : "caught up", work ? "needs_work" : "pass")}
        </div>
      </header>

      <section class="trees-pipeline-focus" data-trees-pipeline-focus>
        <p class="trees-pipeline-kicker">Work next</p>
        ${work && work.count > 0
          ? `<a class="trees-pipeline-focus-count" href="${escapeAttr(treesPipelineListHref(work.id))}" target="_blank" rel="noopener">${formatInteger(work.count)}</a>`
          : `<p class="trees-pipeline-focus-count">${formatInteger(work?.count ?? 0)}</p>`}
        <h2>${escapeHtml(work?.label ?? "Nothing gated")}</h2>
        <p class="trees-pipeline-focus-copy">${escapeHtml(work ? STAGE_COPY[work.id] ?? "" : "No owner, skip, value, Close, or obit hop is waiting.")}</p>
        <dl class="trees-pipeline-focus-meta">
          <div>
            <dt>Unique houses</dt>
            <dd>${formatInteger(tree.properties)}</dd>
          </div>
          <div>
            <dt>On latest scrape</dt>
            <dd>${formatInteger(tree.seenOnLatestScrape)} <small>${escapeHtml(latestDay)}</small></dd>
          </div>
          <div>
            <dt>Not “streets today”</dt>
            <dd>Ever seen on a firm tree</dd>
          </div>
        </dl>
      </section>

      <section class="trees-pipeline-river-wrap" data-trees-pipeline-stages aria-label="exclusive next hops">
        <div class="trees-pipeline-section-head">
          <h2>Where each house sits</h2>
          <p>One hop per house. Large counts are still actionable. Deadends stay in the lane. Bars add up to ${formatInteger(tree.properties)}.</p>
        </div>
        <div class="trees-pipeline-river" role="img" aria-label="Houses by next hop">
          ${activeStages.map((stage) =>
            `<span class="trees-pipeline-seg trees-pipeline-seg--${escapeAttr(stage.id)}" style="flex-grow:${Math.max(stage.count, 1)}" title="${escapeAttr(`${stage.label}: ${stage.count}`)}"><em>${formatInteger(stage.count)}</em></span>`
          ).join("")}
        </div>
        <ol class="trees-pipeline-legend">
          ${tree.stages.map((stage) => {
            const ready = stageReady(stage);
            const deadends = stage.deadends ?? [];
            return `
            <li class="${stage.count === 0 ? "is-empty" : ""}" data-stage="${escapeAttr(stage.id)}">
              <span class="trees-pipeline-dot trees-pipeline-seg--${escapeAttr(stage.id)}"></span>
              <div>
                <strong>${escapeHtml(stage.label)}</strong>
                <small>${escapeHtml(STAGE_COPY[stage.id] ?? "")}</small>
                ${deadends.length ? `<ul class="trees-pipeline-deadends">${deadends.map((row) => `
                  <li data-deadend="${escapeAttr(row.class)}">${row.count > 0
                    ? `<a href="${escapeAttr(treesPipelineListHref(stage.id, row.class))}" target="_blank" rel="noopener"><b>${formatInteger(row.count)}</b></a>`
                    : `<b>${formatInteger(row.count)}</b>`} ${escapeHtml(TREES_PIPELINE_DEADEND_COPY[row.class] ?? labelize(row.class))}</li>`).join("")}</ul>` : ""}
              </div>
              ${ready > 0
                ? `<a data-stage-ready href="${escapeAttr(treesPipelineListHref(stage.id))}" target="_blank" rel="noopener">${formatInteger(ready)}</a>`
                : `<b data-stage-ready>${formatInteger(ready)}</b>`}
            </li>`;
          }).join("")}
        </ol>
      </section>

      ${treesPipelineAuctionTimingHtml(tree)}

      <div class="trees-pipeline-split">
        <section class="trees-pipeline-card" data-trees-pipeline-sources>
          <div class="trees-pipeline-section-head">
            <h2>The seven trees</h2>
            <p>Last seen on that firm’s list. A house can sit on more than one tree.</p>
          </div>
          <ul class="trees-pipeline-trees">
            ${sevenTrees(tree).map((row) => {
              const fresh = isFresh(row.lastSeenAt, tree.latestScrapeAt);
              return `
              <li>
                <strong>${escapeHtml(TREE_SOURCE_LABELS[row.source] ?? row.source)}</strong>
                <b>${formatInteger(row.properties)}</b>
                <small class="${fresh ? "is-fresh" : "is-stale"}">${escapeHtml(fresh ? `On ${formatDay(row.lastSeenAt)} scrape` : row.lastSeenAt ? `Last seen ${formatDay(row.lastSeenAt)}` : "Not seen")}</small>
              </li>`;
            }).join("")}
          </ul>
        </section>
        <section class="trees-pipeline-card" data-trees-pipeline-failures>
          <div class="trees-pipeline-section-head">
            <h2>Why rows are stuck</h2>
            <p>Typed stamps. Stale ones can outnumber the current hop. Generated ${escapeHtml(generated)}.</p>
          </div>
          ${tree.failures.length ? `
          <ul class="trees-pipeline-stuck">
            ${tree.failures.map((row) => `
              <li>
                <b>${formatInteger(row.count)}</b>
                <div>
                  <strong>${escapeHtml(TREES_PIPELINE_DEADEND_COPY[row.class] ?? labelize(row.class))}</strong>
                  <small>${escapeHtml(labelize(row.class))}</small>
                </div>
              </li>`).join("")}
          </ul>` : `<p class="empty">No conveyor failure stamps on tree houses.</p>`}
        </section>
      </div>
  `;
}

function treesPipelineAuctionTimingHtml(tree: TreesPipelineCounts): string {
  const rows = tree.stages.map((stage) => `
            <tr data-auction-stage="${escapeAttr(stage.id)}">
              <th scope="row">${escapeHtml(stage.label)}</th>
              <td>${formatInteger(stage.count)}</td>
              <td>${auctionTimingCell(stage.auctionTiming, "within_30_days")}</td>
              <td>${auctionTimingCell(stage.auctionTiming, "over_30_days")}</td>
              <td>${auctionTimingCell(stage.auctionTiming, "past_or_unknown")}</td>
            </tr>`).join("");
  return `
      <section class="trees-pipeline-card" data-trees-pipeline-auction-breakdown>
        <div class="trees-pipeline-section-head">
          <h2>Auction timing by stage</h2>
          <p>Every house is still counted in its next stage, with timing shown separately.</p>
        </div>
        <div class="trees-pipeline-auction-table-shell">
          <table class="trees-pipeline-auction-table">
            <thead>
              <tr>
                <th scope="col">Stage</th>
                <th scope="col">Total</th>
                <th scope="col">0–30 days</th>
                <th scope="col">&gt;30 days</th>
                <th scope="col">Past / unknown</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
            <tfoot>
              <tr>
                <th scope="row">All stages</th>
                <td>${formatInteger(tree.properties)}</td>
                <td>${auctionTimingCell(tree.auctionTiming, "within_30_days")}</td>
                <td>${auctionTimingCell(tree.auctionTiming, "over_30_days")}</td>
                <td>${auctionTimingCell(tree.auctionTiming, "past_or_unknown")}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
  `;
}

function auctionTimingCell(
  timing: TreesPipelineAuctionTimingCounts | undefined,
  bucket: keyof TreesPipelineAuctionTimingCounts
): string {
  return timing ? formatInteger(timing[bucket]) : "—";
}

function nextWork(tree: TreesPipelineCounts): { id: string; label: string; count: number } | null {
  const stage = tree.stages.find((row) => WORK_STAGE_IDS.includes(row.id) && stageReady(row) > 0) ?? null;
  return stage ? { id: stage.id, label: stage.label, count: stageReady(stage) } : null;
}

function stageReady(stage: TreesPipelineCounts["stages"][number]): number {
  return stage.ready ?? stage.count;
}

function sevenTrees(tree: TreesPipelineCounts): Array<{ source: string; properties: number; lastSeenAt: string | null }> {
  const bySource = new Map(tree.sources.map((row) => [row.source, row]));
  const ordered = TREE_SOURCE_ORDER.map((source) => bySource.get(source) ?? { source, properties: 0, lastSeenAt: null });
  const extra = tree.sources.filter((row) => !(TREE_SOURCE_ORDER as readonly string[]).includes(row.source));
  return [...ordered, ...extra].filter((row) => row.properties > 0);
}

function isFresh(lastSeenAt: string | null, latestScrapeAt: string | null): boolean {
  if (!lastSeenAt || !latestScrapeAt) return false;
  return lastSeenAt.slice(0, 10) === latestScrapeAt.slice(0, 10);
}

function formatDay(value: string | null): string {
  if (!value) return "unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function treesPipelineListHtml(
  payload: TreesPipelineList | null,
  query: { stage: string; deadend: string | null }
): string {
  const title = payload?.label ?? labelize(query.stage);
  const pile = query.deadend
    ? (TREES_PIPELINE_DEADEND_COPY[query.deadend] ?? labelize(query.deadend))
    : "Still ready";
  const rows = payload?.properties ?? [];
  return `
    <main class="trees-pipeline-page" data-trees-pipeline-list>
      <header class="trees-pipeline-top">
        <div>
          <p class="eyebrow">Trees pipeline pile</p>
          <h1>${escapeHtml(title)}</h1>
          <p class="subtle">${escapeHtml(pile)} · ${formatInteger(rows.length)} houses</p>
        </div>
        <a class="button secondary" href="/trees-pipeline">Overview</a>
      </header>
      <section class="trees-pipeline-card">
        ${payload
          ? rows.length
            ? `<ul class="trees-pipeline-property-list">${rows.map((row) => propertyListRow(row)).join("")}</ul>`
            : `<p class="empty">No houses in this pile.</p>`
          : `<p class="empty">This pile could not be loaded.</p>`}
      </section>
    </main>
  `;
}

function propertyListRow(row: TreesPipelineList["properties"][number]): string {
  const address = [row.street, row.city].filter(Boolean).join(", ") || "Address unknown";
  const owner = row.owner || "Owner unknown";
  const sp = row.sp?.trim() || "";
  const inner = `
        <strong>${escapeHtml(sp || "No SP")}</strong>
        <span>${escapeHtml(address)}</span>
        <span>${escapeHtml(owner)}</span>
        <small>${escapeHtml(row.county || "")}</small>`;
  if (!sp) return `<li class="is-plain">${inner}</li>`;
  return `<li><a href="${escapeAttr(`/brief/${encodeURIComponent(sp)}`)}" target="_blank" rel="noopener">${inner}</a></li>`;
}

function treesPipelineUnavailableHtml(): string {
  return `
      <header class="trees-pipeline-top">
        <div>
          <p class="eyebrow">Seven trees</p>
          <h1>Trees pipeline</h1>
        </div>
        <a class="button secondary" href="/">Dashboard</a>
      </header>
      <section class="trees-pipeline-card">
        <h2>Live hopper counts unavailable</h2>
        <p class="subtle">The properties conveyor health endpoint did not return tree counts. This is not a clear queue.</p>
      </section>
  `;
}
